<template>
    <div
        class="md-nbr-demographics layout1 mg-bg"
        :style="{
            ...spaceStyle,
            ...colorStyle,
            ...currentColorStyle,
            background: backgroundColor
        }"
    >
        <module-box
            class="demographics-content"
            :fullColumn="fullColumn"
            :mobileNoMargin="mobileNoMargin"
            noPadding
            noMargin
        >
            <div class="title-wrap">
                <site-title class="mg-title" :title="title" />
                <AttomDesc class="pc" v-if="!isMobileSize" :size="14" />
            </div>
            <div class="demographics-info">
                <div class="tabs">
                    <div class="tab-item" :class="{active: curTab === item.type}" @click="changeTab(item.type)" v-for="item in tabList" :key="item.type">{{ item.label }}</div>
                </div>
                <div class="card-list" v-animated-number="{ duration: 3000, enable: showAnimationEffects }">
                    <div class="card-item" v-for="(card, index) in infoData" :key="index">
                        <div class="card-type">{{ card.title }}</div>
                        <div class="card-num number">{{ card.value || '--' }}</div>
                        <div class="card-tip" v-html="card.desc"></div>
                    </div>
                </div>
                <div
                    class="charts"
                    :style="`transform:translateX(calc(-${
                        curShowTag - 1
                    }*(100% + 30px)))`"
                >
                    <template v-if="curTab === 'population'">
                        <nbr-chart
                            class="age chart-card"
                            type="bar"
                            :subTitle="
                                $t('md-nbr-demographics.medianHtml', {
                                    median: isAgeHasData ? ageMedian : '--'
                                })
                            "
                            :options="ageOptions"
                            :hasData="isAgeHasData"
                            :noDataDesc="$t('md-nbr-demographics.noDataDesc')"
                            :title="$t('md-nbr-demographics.age')"
                        />
                        <div class="nbr-chart layout1 education chart-card">
                            <div class="title-wrap">
                                <div class="title-box">
                                    <span class="title">{{ $t('md-nbr-demographics.educationLevel') }}</span>
                                </div>
                            </div>
                            <template v-if="isDemoEducationHasData">
                                <div class="bar-percent">
                                    <div class="item-percent" :style="{ width: item.ratio + '%', background: item.color }" v-for="(item,index) in educationOptions" :key="index"></div>
                                </div>
                                <div class="education-list">
                                    <div class="education-item" v-for="item in educationOptions" :key="item.type">
                                        <div class="item-left">
                                            <div class="item-box" :style="{ background: item.color }"></div>
                                            <div class="item-label">{{ item.label }}</div>
                                        </div>
                                        <div class="item-right">{{ item.value + '%' }}</div>
                                    </div>
                                </div>
                            </template>
                            <div class="empty-data" v-else>
                                <i class="iconfont icon-activities_04"></i>
                                <div class="no-data">{{ $t('md-nbr-demographics.noDataDesc') }}</div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <nbr-chart
                            class="income chart-card"
                            :class="{'empty': !isAnnualIncomeHasData}"
                            type="bar"
                            :subTitle="
                                $t('md-nbr-demographics.averageHtml', {
                                    incomeAverage: isAnnualIncomeHasData ? incomeAverage : '--'
                                })
                            "
                            :options="incomeOptions"
                            :noDataDesc="$t('md-nbr-demographics.noDataDesc')"
                            :hasData="isAnnualIncomeHasData"
                            :title="$t('md-nbr-demographics.annualIncome')"
                        />
                        <nbr-chart
                            class="employment chart-card"
                            :class="{'empty': !isEmploymentHasData}"
                            type="pie"
                            :options="employmentOptions"
                            :noDataDesc="$t('md-nbr-demographics.noDataDesc')"
                            :hasData="isEmploymentHasData"
                            :title="$t('md-nbr-demographics.employment')"
                        />
                    </template>
                </div>
                <AttomDesc class="mobile" v-if="isMobileSize" :size="12" />
            </div>
        </module-box>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { usePageStore } from '@/store';
import common from '../index';
import nbrChart from '../../../components/nbr-chart/layout1.vue';
import { numberFormat } from '../../../common/format';

export default {
    mixins: [common],
    data: function() {
        return {
            title: '',
            infoData: [],
            infoEmpty: true,
            genderOptions: null,
            educationOptions: null,
            ageOptions: null,
            raceOptions: null,
            incomeOptions: null,
            employmentOptions: null,
            ageMedian: '',
            incomeAverage: '',
            curShowTag: 1,
            curTab: 'population',
            showAnimationEffects: true
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            'page': store => store.page || {},
            'currencySymbol': store => store.currency.symbol,
            'usdCurrencyRate': store => store.usdCurrencyRate
        }),
        Data({ page }) {
            return page;
        },
        themeColor({ page }) {
            return page.themeColor || '#ff781d';
        },
        tabList({ $t }) {
            return [
                { type: 'population', label: $t('md-nbr-demographics.population') },
                { type: 'employment', label: $t('md-nbr-demographics.employment') }
            ]
        }
    },
    methods: {
        changeTab(tab) {
            this.curTab = tab
        },
        resetdata: function() {
            this.infoData = [];
            this.genderOptions = null;
            this.educationOptions = null;
            this.ageOptions = null;
            this.raceOptions = null;
            this.incomeOptions = null;
            this.employmentOptions = null;
        },
        setChartData(data) {
            this.setInfoData(data);
            data.education && this.setEducationOptions(data.education);
            data.age && this.setAgeOptions(data.age);
            data.income && this.setIncomeOptions(data.income);
            data.employment && this.setEmploymentOptions(data.employment);
        },
        setInfoData(data) {
            this.infoEmpty = false;
            let _this = this
            let genderValue = '--'
            let occupancyValue = '--'
            let householdsPercent = ''
            if (data.households.householdsOccupancyChildren) {
                householdsPercent = Number(
                    data.households.householdsOccupancyChildren
                ).toFixed(0) + '%'
            }
            if (data.gender?.genderMale && data.gender?.genderFemale) {
                genderValue = `${Number(data.gender.genderMale).toFixed(0)}% / ${Number(data.gender.genderFemale).toFixed(0)}%`
            }
            if (data.households?.householdsOccupancyOwned && data.households?.householdsOccupancyRented) {
                occupancyValue = `${Number(data.households.householdsOccupancyOwned).toFixed(0)}% / ${Number(data.households.householdsOccupancyRented).toFixed(0)}%`
            }

            const infoData = [
                {
                    get title() {
                        return _this.$t('md-nbr-demographics.population');
                    },
                    value: numberFormat(
                        data.population.populationCurrent || data.population.population2010,
                        true,
                        1
                    ),
                    get desc() {
                        if (data.population.populationCurrent) {
                            return _this.$t('md-nbr-demographics.in2020', {
                                population: numberFormat(
                                    data.population.populationCurrent,
                                    true
                                )
                            })
                        } else {
                            return _this.$t('md-nbr-demographics.in2010', {
                                population: numberFormat(
                                    data.population.population2010,
                                    true
                                )
                            })
                        }
                    }
                },
                {
                    get title() {
                        return _this.$t('md-nbr-demographics.density');
                    },
                    value: numberFormat(
                        data.population.populationDensity,
                        true,
                        1
                    ),
                    get desc() {
                        return _this.$t('md-nbr-demographics.perSquareMile')
                    }
                },
                {
                    get title() {
                        return _this.$t('md-nbr-demographics.households');
                    },
                    value: numberFormat(
                        data.households.householdsTotal,
                        true,
                        1
                    ),
                    get desc() {
                        return _this.$t('md-nbr-demographics.percentWithChildrenNew', {
                            percent: householdsPercent
                        })
                    }
                },
                {
                    get title() {
                        return _this.$t('md-nbr-demographics.gender');
                    },
                    value: genderValue,
                    get desc() {
                        return _this.$t('md-nbr-demographics.menVsWomen')
                    }
                },
                {
                    get title() {
                        return _this.$t('md-nbr-demographics.occupancy');
                    },
                    value: occupancyValue,
                    get desc() {
                        return _this.$t('md-nbr-demographics.ownedVsRented')
                    }
                }
            ];
            this.infoData = infoData;
        },
        setEducationOptions(data) {
            const numbers = [
                data.educationAssociate || 0,
                data.educationBachelor || 0,
                data.educationGraduate || 0,
                data.educationHighschool || 0
            ]
            const totalNumbers = numbers.reduce((prev, curr) => {
                return Number(prev) + Number(curr);
            }, 0);
        
            const list = [
                { 
                    type: 'associate', 
                    color: this.currentColorStyle['--g-primary-color_25'],
                    value: data.educationAssociate, 
                    label: this.$t('md-nbr-demographics.associate'),
                    ratio: ((Number(data.educationAssociate || 0) / totalNumbers) * 100).toFixed(0)
                },
                { 
                    type: 'bachelor', 
                    color: this.currentColorStyle['--g-primary-color_40'],
                    value: data.educationBachelor, 
                    label: this.$t('md-nbr-demographics.bachelor'),
                    ratio: ((Number(data.educationBachelor || 0) / totalNumbers) * 100).toFixed(0)
                },
                { 
                    type: 'graduate', 
                    color: this.currentColorStyle['--g-primary-color_55'],
                    value: data.educationGraduate, 
                    label: this.$t('md-nbr-demographics.graduate'),
                    ratio: ((Number(data.educationGraduate || 0) / totalNumbers) * 100).toFixed(0)
                },
                { 
                    type: 'highschool', 
                    color: this.currentColorStyle['--g-primary-color_70'],
                    value: data.educationHighschool, 
                    label: this.$t('md-nbr-demographics.highschool'),
                    ratio: ((Number(data.educationHighschool || 0) / totalNumbers) * 100).toFixed(0)
                }
            ];
            this.educationOptions = list;
        },
        setAgeOptions(data) {
            this.ageMedian = Number(data.ageMedian).toFixed(0);
            const colors = [
                this.currentColorStyle['--g-primary-color_25'],
                this.currentColorStyle['--g-primary-color_40'],
                this.currentColorStyle['--g-primary-color_55'],
                this.currentColorStyle['--g-primary-color_70'],
                this.currentColorStyle['--g-primary-color_85'],
                this.currentColorStyle['--g-primary-color_100']
            ];
            const yAxisData = [
                '0-11',
                '12-24',
                '25-34',
                '35-44',
                '45-64',
                '65+'
            ];
            const seriesData = [
                Number(data.ageY0to5) + Number(data.ageY6to11),
                Number(data.ageY12to17) + Number(data.ageY18to24),
                Number(data.ageY25to34),
                Number(data.ageY35to44),
                Number(data.ageY45to54) + Number(data.ageY55to64),
                Number(data.ageY65to74) +
                    Number(data.ageY75to84) +
                    Number(data.ageY85up)
            ].map((item, index) => ({
                value: item,
                itemStyle: {
                    normal: {
                        color: colors[index],
                        barBorderRadius: [0, 5, 5, 0]
                    }
                },
                emphasis: {
                    itemStyle: {
                        color: colors[index]
                    }
                }
            }));
            const chartOption = {
                grid: {
                    left: 0,
                    top: 0,
                    bottom: 0,
                    right: 50,
                    containLabel: true
                },
                yAxis: [
                    {
                        type: 'category',
                        axisLabel: {
                            show: true,
                            color: this.currentColorStyle['--g-primary-color_80'],
                            fontSize: 14,
                            fontFamily: 'font-normal'
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                type: 'dashed',
                                color: 'rgba(218, 218, 218, 0.5)'
                            }
                        },
                        axisTick: {
                            show: false
                        },
                        offset: 0,
                        data: yAxisData.reverse()
                    }
                ],
                xAxis: {
                    type: 'value',
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    splitLine: { show: false }
                },
                series: [
                    {
                        label: {
                            normal: {
                                show: true,
                                color: this.currentColorStyle['--g-primary-color_100'],
                                padding: [0, 0, 0, this.isMobileSize ? 5 : 10],
                                fontSize: 14,
                                fontFamily: 'font-medium',
                                position: 'right',
                                formatter: function(params) {
                                    return (
                                        Number(params.value).toFixed(0) + '%'
                                    );
                                }
                            }
                        },
                        data: seriesData.reverse(),
                        type: 'bar',
                        barWidth: 20
                    }
                ]
            };
            this.ageOptions = chartOption;
        },
        setIncomeOptions(data) {
            this.incomeAverage =
                `${this.currencySymbol} ` +
                numberFormat(data.incomeAverage, true);
            const colors = [
                this.currentColorStyle['--g-primary-color_10'],
                this.currentColorStyle['--g-primary-color_25'],
                this.currentColorStyle['--g-primary-color_40'],
                this.currentColorStyle['--g-primary-color_55'],
                this.currentColorStyle['--g-primary-color_70'],
                this.currentColorStyle['--g-primary-color_85'],
                this.currentColorStyle['--g-primary-color_100']
            ];
            const baseDollarNumber = [
                35000, 50000, 75000, 100000, 150000, 200000
            ];
            //  Interval multiplied by exchange rate
            if (!this.usdCurrencyRate) {
                this.usdCurrencyRate = 1;
            }
            const newCurrencyNumber = baseDollarNumber.map(item => {
                //  in the case of M,  just keep 1 decimal places
                let target = numberFormat(item * this.usdCurrencyRate, true, 1);
                if (target.indexOf('M') > -1) {
                    return target;
                } else {
                    return numberFormat(item * this.usdCurrencyRate, true, 0);
                }
            });
            const yAxisData = [
                this.currencySymbol + `0-${newCurrencyNumber[0]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[0]}-${newCurrencyNumber[1]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[1]}-${newCurrencyNumber[2]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[2]}-${newCurrencyNumber[3]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[3]}-${newCurrencyNumber[4]}`,
                this.currencySymbol +
                    `${newCurrencyNumber[4]}-${newCurrencyNumber[5]}`,
                this.currencySymbol + `${newCurrencyNumber[5]}+`
            ];
            const seriesData = [
                Number(data.incomePercent0to15) +
                    Number(data.incomePercent15to25) +
                    Number(data.incomePercent25to35),
                Number(data.incomePercent35to50),
                Number(data.incomePercent50to75),
                Number(data.incomePercent75to100),
                Number(data.incomePercent100to125) +
                    Number(data.incomePercent125to150),
                Number(data.incomePercent150to200),
                Number(data.incomePercent200up)
            ];
            seriesData.map((item, index) => {
                seriesData[index] = {
                    value: item,
                    itemStyle: {
                        color: colors[index],
                        barBorderRadius: [0, 5, 5, 0]
                    },
                    emphasis: {
                        itemStyle: {
                            color: colors[index]
                        }
                    }
                };
            });
            const chartOption = {
                grid: {
                    left: 0,
                    top: 0,
                    bottom: 0,
                    right: 50,
                    containLabel: true
                },
                yAxis: [
                    {
                        type: 'category',
                        axisLabel: {
                            show: true,
                            color: this.currentColorStyle['--g-primary-color_80'],
                            fontSize: 14,
                            fontFamily: 'font-normal'
                        },
                        axisLine: {
                            show: true,
                            lineStyle: {
                                type: 'dashed',
                                color: 'rgba(218, 218, 218, 0.5)'
                            }
                        },
                        axisTick: {
                            show: false
                        },
                        offset: 0,
                        data: yAxisData.reverse()
                    }
                ],
                xAxis: {
                    type: 'value',
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    splitLine: { show: false }
                },
                series: [
                    {
                        label: {
                            normal: {
                                show: true,
                                color: this.currentColorStyle['--g-primary-color_100'],
                                padding: [0, 0, 0, this.isMobileSize ? 5 : 10],
                                fontSize: 14,
                                fontFamily: 'font-medium',
                                position: 'right',
                                formatter: function(params) {
                                    return (
                                        Number(params.value).toFixed(0) + '%'
                                    );
                                }
                            }
                        },
                        data: seriesData.reverse(),
                        type: 'bar',
                        barWidth: 20
                    }
                ]
            };
            this.incomeOptions = chartOption;
        },
        setEmploymentOptions(data) {
            const colors = [
                this.currentColorStyle['--g-primary-color_100'],
                this.currentColorStyle['--g-primary-color_85'],
                this.currentColorStyle['--g-primary-color_70'],
                this.currentColorStyle['--g-primary-color_55'],
                this.currentColorStyle['--g-primary-color_40'],
                this.currentColorStyle['--g-primary-color_25']
            ];
            const newData = [];
            const seriesData = [];
            let other = 100;
            for (let name in data) {
                newData.push({
                    name: name.replace('employment', ''),
                    value: Number(data[name])
                });
            }
            newData.sort(function(a, b) {
                return b.value - a.value;
            });
            for (let i = 0; i < 5; i++) {
                seriesData.push(newData[i]);
                other = other - newData[i].value;
            }
            seriesData.push({
                name: 'Other',
                value: (other || '').toFixed(2)
            });
            data['employmentOther'] = other;
            seriesData.sort(function(a, b) {
                return b.value - a.value;
            });
            seriesData.forEach((item, index) => {
                item.itemStyle = {
                    color: colors[index]
                };
                item.emphasis = {
                    itemStyle: {
                        color: colors[index]
                    }
                };
            });
            const chartOption = {
                legend: {
                    show: false
                },
                labelLine: {
                    length: 100,
                    length2: 50,
                    lineStyle: {
                        color: 'rgba(218, 218, 218, 1)'
                    }
                },
                label: {
                    formatter: '{name|{b}}\n{time|{c}%}',
                    alignTo: 'labelLine',
                    edgeDistance: 10,
                    lineHeight: 18,
                    fontSize: 12,
                    rich: {
                        name: {
                            fontSize: 12,
                            color: this.colorMap?.text || 'rgba(80, 80, 80, 1)'
                        },
                        time: {
                            fontSize: 14,
                            marginTop: 10,
                            color: this.currentColorStyle['--g-primary-color_100']
                        }
                    },
                },
                series: [
                    {
                        data: seriesData,
                        type: 'pie',
                        center: ['50%', '50%'],
                        radius: ['40%', '60%'],
                        avoidLabelOverlap: false,
                        legendHoverLink: false,
                        hoverOffset: 0,
                        cursor: 'default',
                        label: {
                            normal: {
                                show: true
                            },
                            emphasis: {
                                show: true
                            }
                        },
                        labelLine: {
                            normal: {
                                show: true
                            }
                        }
                    }
                ],
            };
            this.employmentOptions = chartOption;
        },
        showTagById(id) {
            // PC terminal fixed display
            if (!this.isMobileSize) {
                return true; 
            }
            if (this.isMobileSize && this.curShowTag == id) {
                return true; 
            }
            return false;
        }
    },
    components: {
        nbrChart,
    }
};
</script>

<style lang="scss">
@import '../../../theme/media.scss';
@import '@/theme/mixin.scss';
.md-nbr-demographics.layout1 {
    width: 100%;
    .title-wrap {
        position: relative;
        .mg-title {
            color: var(--g-primary-color);
        }
    }
    .attom-desc {
        &.pc {
            position: absolute;
            right: 0;
            bottom: 20px;
            z-index: 100;
        }
        font-size: 14px;
        line-height: 1.5;
        color: var(--g-tip-color);
        a {
            color: var(--color-link, var(--color-theme, #03c9ff));
            cursor: pointer;
        }
    }
    h2 {
        line-height: 100%;
    }
    @media (min-width: 801px) {
        padding: 0 50px;
    }
    @media (max-width: 800px) {
        padding: 0 15px;
        .attom-desc { 
            position: relative;
            margin-top: 15px;
            font-size: 12px;
        }
    }
    .demographics-content {
        overflow: visible;
        position: relative;
    }
    .demographics-info {
        display: flex;
        flex-direction: column;
        .tabs {
            display: flex;
            margin-bottom: 20px;
            gap: 30px;
            margin-top: 20px;
            .tab-item {
                display: flex;
                align-items: flex-start;
                height: 45px;
                font-size: 18px;
                color: var(--g-primary-color_50);
                cursor: pointer;
                &.active {
                    color: var(--g-primary-color);
                    border-bottom: 3px solid var(--g-primary-color);
                }
            }
        }
        .card-list {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
            margin-bottom: 26px;
            .card-item {
                border: 1px solid rgba(218, 218, 218, 0.5);
                border-radius: 12px;
                padding: 20px;
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: calc(20% - 16px);
                @media (max-width: 1200px) { 
                    width: calc(25% - 15px);
                }
                @media (max-width: 1080px) { 
                    width: calc(33.33% - 14px);
                }
                @media (max-width: 800px) { 
                    width: calc(50% - 8px);
                }
            }
            .card-type {
                font-size: 16px;
                line-height: 1.5;
                color: var(--g-primary-color);
            }
            .card-num {
                font-size: 24px;
                line-height: 1.5;
                color: var(--g-primary-color);
                font-family: var(--font-medium);
                font-variant-ligatures: none;
                font-feature-settings: "liga" 0;
            }
            .card-tip {
                font-size: 12px;
                line-height: 1.5;
                color: var(--g-tip-color);
                font-variant-ligatures: none;
                font-feature-settings: "liga" 0;
            }
        }
    }
    .charts {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        .nbr-chart {
            &.education {
                .bar-percent {
                    width: 100%;
                    height: 20px;
                    border-radius: 8px;
                    overflow: hidden;
                    display: flex;
                    gap: 2px;
                    margin-top: 20px;
                }
                .education-list {
                    display: flex;
                    flex-direction: column;
                    margin-top: 14px;
                    .education-item {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        height: 60px;
                        box-sizing: border-box;
                        border-bottom: 1px solid rgba(218, 218, 218, 0.5);
                        &:nth-last-child(1) {
                            border-bottom: none;
                        }
                        .item-left {
                            display: flex;
                            .item-box {
                                width: 20px;
                                height: 20px;
                                border-radius: 6px;
                                margin-right: 10px;
                            }
                            .item-label {
                                font-size: 14px;
                                color: var(--g-primary-color);
                            }
                        }
                        .item-right {
                            font-size: 14px;
                            color: var(--g-primary-color);
                            // font-family: var(--font-medium);
                            font-family: auto;
                        }
                    }
                }
            }
        }
    }
    .chart-card {
        width: calc(50% - 10px);
        border-radius: 12px;
        border: 1px solid rgba(218, 218, 218, 0.5);
        box-shadow: none;
        background-color: transparent;
        padding: 20px;
        height: 380px;
        &.income, &.employment {
            height: 430px;
            &.empty {
                height: 380px;
            }
        }
        .chart-box {
            margin-top: 20px;
        }
    }
    @include media-800 {
        .demographics-info {
            flex-direction: column;
            .tabs {
                margin-top: 0px;
                margin-bottom: 20px;
                gap: 20px;
                .tab-item {
                    height: 40px;
                    font-size: 14px;
                }
            }
            .card-list {
                gap: 15px;
                margin-bottom: 15px;
                .card-item {
                    padding: 9px 15px;
                    gap: 5px;
                }
                .card-type {
                    font-size: 14px;
                }
                .card-num {
                    font-size: 18px;
                }
                .card-tip {
                    font-size: 12px;
                }
            }
        }
        .charts {
            display: flex;
            flex-wrap: nowrap;
            transition: transform 0.3s ease;
            flex-direction: column;
            .chart-card {
                flex-shrink: 0;
                margin-top: 0;
                &:not(:last-child) {
                    margin-bottom: 15px;
                }
            }
        }

        .chart-card { 
            width: 100%;
            height: 324px;
            padding: 20px 15px;
            &.age {
                height: 324px;
            }
            &.education {
                height: 338px;
                .education-list {
                    margin-bottom: -20px;
                }
            }
            &.income {
                height: 375px;
            }
            &.employment {
                height: 320px;
            }
        }
    }
}
</style>
